if (!customElements.get('signature-acknowledgment')) {
  customElements.define(
    'signature-acknowledgment',
    class SignatureAcknowledgment extends HTMLElement {
      connectedCallback() {
        this._inputs = this.querySelectorAll('.signature-ack__input');
        this._errorEl = this.querySelector('.signature-ack__error');
        this._context = this.dataset.context || 'page';
        this._form = document.getElementById(
          this._context === 'drawer' ? 'CartDrawer-Form' : 'cart'
        );

        this._inputs.forEach((input) =>
          input.addEventListener('change', this._onChange.bind(this))
        );

        if (this._form) {
          this._onSubmit = this._handleSubmit.bind(this);
          this._form.addEventListener('submit', this._onSubmit);
        }

        // Defer button lookup to next tick — when innerHTML replaces a block of HTML,
        // siblings after this element (including CartDrawer-Checkout) may not be in
        // the DOM yet when connectedCallback fires.
        setTimeout(() => {
          this._checkoutButton = document.getElementById(
            this._context === 'drawer' ? 'CartDrawer-Checkout' : 'checkout'
          );
          this._syncButton();
        }, 0);

        // If the cart dropped below the threshold while a value was still saved
        // server-side, scrub it now so it can't ride along to checkout. Hide
        // the dynamic checkout buttons (Shop Pay / PayPal / Google Pay) while
        // the clear is in flight — those bypass the cart form submit event,
        // so they would otherwise snapshot the cart with the stale attribute.
        if (!this._isActive && this.dataset.current) {
          this._setDynamicCheckoutHidden(true);
          this._clearSignature().finally(() => this._setDynamicCheckoutHidden(false));
        }
      }

      _setDynamicCheckoutHidden(hidden) {
        document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((el) => {
          el.style.display = hidden ? 'none' : '';
        });
      }

      disconnectedCallback() {
        if (this._form && this._onSubmit) {
          this._form.removeEventListener('submit', this._onSubmit);
        }
      }

      get _isActive() {
        return !this.classList.contains('hidden');
      }

      get _selectedValue() {
        const checked = [...this._inputs].find((i) => i.checked);
        return checked ? checked.value : '';
      }

      _syncButton() {
        this._setCheckoutDisabled(this._isActive && !this._selectedValue);
      }

      _setCheckoutDisabled(disabled) {
        if (!this._checkoutButton) return;
        this._checkoutButton.disabled = disabled;
        this._checkoutButton.setAttribute('aria-disabled', String(disabled));
      }

      async _onChange() {
        this._hideError();
        this._setCheckoutDisabled(false);
        await this._persist(this._selectedValue);
      }

      async _persist(value) {
        const cartRes = await fetch(`${routes.cart_url}.js`, { cache: 'no-cache' });
        const cart = await cartRes.json();
        const updatedNote = this._buildNote(cart.note, value);

        return fetch(`${routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: { 'Signature Requirement': value },
            note: updatedNote,
          }),
        });
      }

      _clearSignature() {
        this._inputs.forEach((i) => (i.checked = false));
        this._clearPromise = this._persist('').then(() => {
          this.dataset.current = '';
          this._clearPromise = null;
        });
        return this._clearPromise;
      }

      _buildNote(currentNote, value) {
        const prefix = 'Signature Requirement: ';
        const lines = (currentNote || '').split('\n').filter((l) => !l.startsWith(prefix));
        if (value) lines.push(`${prefix}${value}`);
        return lines.join('\n').trim();
      }

      _handleSubmit(event) {
        if (this._isActive) {
          if (!this._selectedValue) {
            event.preventDefault();
            this._showError();
            this._inputs[0]?.focus();
          }
          return;
        }

        // Below threshold: if a stale selection survives in cart attributes/note
        // or in the radio inputs, OR a clear is already in flight, wait for the
        // server to confirm the clear before letting checkout proceed.
        const hasStale = this.dataset.current || [...this._inputs].some((i) => i.checked);
        if (!hasStale && !this._clearPromise) return;

        event.preventDefault();
        const clear = this._clearPromise || this._clearSignature();
        clear.finally(() => this._form.submit());
      }

      _showError() {
        this._errorEl?.classList.remove('hidden');
        this.classList.add('signature-ack--invalid');
      }

      _hideError() {
        this._errorEl?.classList.add('hidden');
        this.classList.remove('signature-ack--invalid');
      }
    }
  );
}
